import type { CallMeVariant } from '../../../../recoil/types/opcTypes';
import { Button, ListBox } from '@flos/react-ui';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { opcInfoAtom } from '../../../../recoil/atoms/opcInfoAtom';
import { useHippoText } from '../../../../hooks/useHippoText';
import { type CoverageOpcDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { type CoverageItem } from '../../../../constants/hippoTypes';
import { useCallback } from 'react';
import { type AdjustCoverageAction } from '../../types';
import CoverageContent from '../CoverageContent/CoverageContent';
import {
    ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
    MVP_COMPREHENSIVE_AUDIO_TEL,
    PRH_PRIVATE_CONTENT_SUM_INSURED_TERM,
    UVP_CONTENT_SUM
} from '../../../../constants/term';
import { ACC_DEATH_COVERAGE } from '../../../../constants/coverage';
import { callMeVariantOpcAtom } from '../../../../recoil/atoms/callMeVariantOpcAtom';
import useOpcJob from '../../../../hooks/useOpcJob';
import { getCoverableInfoSelector } from '../../../../recoil/selectors/getCoverableInfoSelector';
import { getTermFromCoverage } from '../../../../utils/termsUtil';
import { IS_OPC_REMOVE_AVAILABLE, OPC_REMOVE_LOBS } from '../../config/OpcConfig';

const isCallMeModalRequiredForCoverage = (coverageType: string): boolean => {
    const callMeModalCoverages = [ACC_DEATH_COVERAGE];
    return callMeModalCoverages.includes(coverageType);
};

const getCallMeModalVariant = (coverageType: string): CallMeVariant => {
    switch (coverageType) {
        case ACC_DEATH_COVERAGE:
            return 'addDeathCov';
        default:
            return 'default';
    }
};

const isMandatoryOrForcedDeductibleType = (value: string): boolean => {
    const mandatoryOrForcedValues = ['Obligatorisk', 'Tvungen', 'Ja'];
    return mandatoryOrForcedValues.includes(value);
};

export default function CoverageDetailBox({
    coverage,
    coverageContent,
    onOpenModal
}: {
    coverage: CoverageOpcDTO;
    coverageContent: CoverageItem | undefined;
    onOpenModal: () => void;
}): React.JSX.Element {
    const getHippoText = useHippoText();
    const [opcInformation, setOpcInformation] = useRecoilState(opcInfoAtom);
    const setCallMeVariant = useSetRecoilState(callMeVariantOpcAtom);
    const {
        createPolicyChangeWithRenewalDateForOPC,
        withdrawCurrentOPCShoppingBasketIfNotExisting
    } = useOpcJob();
    const isNaebrandAgreement = useRecoilValue(getCoverableInfoSelector).isNaebrandAgreement;
    const isNotRequiredCoverage = !coverage.required;
    const deductibleTerm = coverage.terms?.find((term) => term.isEditableDeductible);
    const isMandatoryOrForcedTerm = coverage.terms?.some((term) =>
        isMandatoryOrForcedDeductibleType(term.value ?? '')
    );
    const canEditDeductibleTerm =
        !!deductibleTerm && !isMandatoryOrForcedTerm && !isNaebrandAgreement;
    const sumTerm = coverage.terms?.find((term) => term.isEditableSum);
    const hasSumInsuredEditable = !!sumTerm;

    const accInjuryDegreeTerm = getTermFromCoverage(coverage, ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM);
    const hasAccInjuryDegreeTerm =
        !!accInjuryDegreeTerm && opcInformation.productName === 'top_acc_accident';

    const mvpAudioTelcoTerm = getTermFromCoverage(coverage, MVP_COMPREHENSIVE_AUDIO_TEL);
    const mvpAudioTelcoTermEditable = mvpAudioTelcoTerm?.isEditableSum;

    const prhPrivateContentSum = getTermFromCoverage(
        coverage,
        PRH_PRIVATE_CONTENT_SUM_INSURED_TERM
    );
    const prhPrivateContentSumEditable = prhPrivateContentSum?.isEditableSum;

    const uvpContentSum = getTermFromCoverage(coverage, UVP_CONTENT_SUM);
    const uvpContentSumEditable = uvpContentSum?.isEditableSum;

    const handleActionClick = useCallback((actionType: AdjustCoverageAction): void => {
        setOpcInformation((previousInfo) => {
            const updatedInfo = {
                ...previousInfo,
                coverageId: coverage.type,
                adjustAction: actionType
            };
            switch (actionType) {
                case 'Deductible':
                    updatedInfo.termId = deductibleTerm?.publicId;
                    break;
                case 'Limit':
                    updatedInfo.termId = sumTerm?.publicId;
                    break;
                case 'InjuryDegree':
                    updatedInfo.termId = accInjuryDegreeTerm?.publicId;
                    break;
                case 'AudioTelcoDropdown':
                    updatedInfo.termId = mvpAudioTelcoTerm?.publicId;
                    break;
                case 'PRHPrivateContentSum':
                    updatedInfo.termId = prhPrivateContentSum?.publicId;
                    break;
                case 'UVPContentSum':
                    updatedInfo.termId = uvpContentSum?.publicId;
                    break;
                default:
                    break;
            }
            return updatedInfo;
        });

        onOpenModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRemoveClick = async (): Promise<void> => {
        if (isCallMeModalRequiredForCoverage(coverage.type ?? '')) {
            setCallMeVariant(getCallMeModalVariant(coverage.type ?? ''));
            return;
        }
        if (opcInformation.isFromDAFlow) {
            handleActionClick('Remove');
            return;
        }
        await withdrawCurrentOPCShoppingBasketIfNotExisting();
        await createPolicyChangeWithRenewalDateForOPC();
        handleActionClick('Remove');
    };

    const EditCoverageAction = (): React.JSX.Element => {
        return (
            <div className="u-flex-box u-flex-box--center u-flex-box--gap-xsmall">
                {canEditDeductibleTerm && (
                    <Button
                        theme="primary"
                        minWidth="wider"
                        onClick={() => {
                            handleActionClick('Deductible');
                        }}
                    >
                        {getHippoText('DSS-COVERABLE-DETAILS-ADAPT-DEDUCTIBLE')}
                    </Button>
                )}
                {hasAccInjuryDegreeTerm && (
                    <Button
                        theme="primary"
                        minWidth="wider"
                        onClick={() => {
                            handleActionClick('InjuryDegree');
                        }}
                    >
                        {getHippoText('DSS-EDIT-EXISTING-COVERAGES-INJURY-DEGREE')}
                    </Button>
                )}
                {hasSumInsuredEditable &&
                    !mvpAudioTelcoTermEditable &&
                    !prhPrivateContentSumEditable &&
                    !uvpContentSumEditable && (
                        <Button
                            theme="secondary"
                            minWidth="wider"
                            onClick={() => {
                                handleActionClick('Limit');
                            }}
                        >
                            {getHippoText('DSS-COVERABLE-DETAILS-ADAPT-SUM')}
                        </Button>
                    )}
                {mvpAudioTelcoTermEditable && (
                    <Button
                        theme="secondary"
                        minWidth="wider"
                        onClick={() => {
                            handleActionClick('AudioTelcoDropdown');
                        }}
                    >
                        {getHippoText('DSS-COVERABLE-DETAILS-ADAPT-SUM')}
                    </Button>
                )}
                {prhPrivateContentSumEditable && (
                    <Button
                        theme="secondary"
                        minWidth="wider"
                        onClick={() => {
                            handleActionClick('PRHPrivateContentSum');
                        }}
                    >
                        {getHippoText('DSS-COVERABLE-DETAILS-ADAPT-SUM')}
                    </Button>
                )}
                {uvpContentSumEditable && (
                    <Button
                        theme="secondary"
                        minWidth="wider"
                        onClick={() => {
                            handleActionClick('UVPContentSum');
                        }}
                    >
                        {getHippoText('DSS-COVERABLE-DETAILS-ADAPT-SUM')}
                    </Button>
                )}
                {IS_OPC_REMOVE_AVAILABLE &&
                    OPC_REMOVE_LOBS.includes(opcInformation.productName) &&
                    isNotRequiredCoverage && (
                        <Button
                            theme="secondary"
                            minWidth="wider"
                            onClick={() => {
                                void handleRemoveClick();
                            }}
                        >
                            {getHippoText('DSS-COVERABLE-DETAILS-REMOVE-COVERAGE')}
                        </Button>
                    )}
            </div>
        );
    };

    if (!coverage.name) {
        return <></>;
    }
    return (
        <div className="u-flex-box u-flex-box--col" id={'coverageListBox'}>
            <ListBox
                key={coverage.name}
                title={coverage.name}
                subtitle={coverageContent?.subtitle}
                infoArea={<EditCoverageAction />}
                isOpen={false}
            >
                <CoverageContent coverageContent={coverageContent} />
            </ListBox>
        </div>
    );
}
